// src/components/Mypage/AddressesPage.jsx
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import PageHeader from './PageHeader';
import DeliveryAddressModal from './DeliveryAddressModal';
import AlertModal from '../common/AlertModal';
import './medichat_mypage.scss';

const actionBtnBase = {
  display: 'block', width: '100%', padding: '16px',
  background: 'none', border: 'none', fontSize: '17px',
  textAlign: 'center', cursor: 'pointer', color: '#007AFF',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const AddressesPage = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [pressedId, setPressedId] = useState(null);

  const load = async () => {
    try {
      const { data } = await axiosInstance.get('/api/medichat/delivery_addresses');
      setList(data?.addresses || []);
    } catch { setList([]); }
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (id) => {
    setBusy(true);
    try { await axiosInstance.post(`/api/medichat/delivery_addresses/${id}/set_default`); await load(); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/medichat/delivery_addresses/${deleteTarget.id}`);
      setDeleteTarget(null); await load();
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.message || '削除に失敗しました' });
    } finally { setDeleting(false); }
  };

  const fullAddress = (a) => [a.prefecture, a.city, a.address_line1, a.address_line2].filter(Boolean).join(' ');

  return (
    <div className="mypage-container">

      <PageHeader
        title="配送先住所"
        right={
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            style={{ background: 'none', border: 'none', fontSize: '14px', color: '#6366f1', cursor: 'pointer', padding: '8px 0', fontFamily: 'inherit', fontWeight: '600' }}
          >追加</button>
        }
      />

      <div style={{ padding: '20px 16px 40px' }}>
        {!list ? (
          <div style={{ textAlign: 'center', color: '#8e8e93', padding: '60px 0', fontSize: '15px' }}>読み込み中…</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: '80px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📦</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>住所が未登録です</div>
            <div style={{ fontSize: '15px', color: '#8e8e93', marginBottom: '28px' }}>お薬の配送先住所を登録してください</div>
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              style={{ background: '#007AFF', color: '#fff', border: 'none', borderRadius: '14px', padding: '14px 32px', fontSize: '17px', fontWeight: '600', cursor: 'pointer' }}
            >住所を追加</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {list.map((a) => (
              <div
                key={a.id}
                onMouseDown={() => setPressedId(a.id)}
                onMouseUp={() => setPressedId(null)}
                onMouseLeave={() => setPressedId(null)}
                onTouchStart={() => setPressedId(a.id)}
                onTouchEnd={() => setPressedId(null)}
                onClick={() => setActionTarget(a)}
                style={{
                  background: '#fff',
                  borderRadius: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: a.is_default
                    ? '0 0 0 2px #4f46e5, 0 2px 8px rgba(0,0,0,0.06)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                  opacity: pressedId === a.id ? 0.75 : 1,
                  transition: 'opacity 0.1s',
                }}
              >
                {/* ヘッダー行: デフォルトバッジ + chevron */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  {a.is_default ? (
                    <span style={{
                      fontSize: '11px', fontWeight: '700',
                      color: '#4f46e5', background: '#eef2ff',
                      padding: '2px 8px', borderRadius: '99px',
                    }}>デフォルト</span>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#aaa' }}>配送先</span>
                  )}
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1l6 6-6 6" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                {/* 住所本体 */}
                {(a.prefecture || a.city) && (
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                    {[a.prefecture, a.city].filter(Boolean).join(' ')}
                  </div>
                )}
                {(a.address_line1 || a.address_line2) && (
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', lineHeight: '1.4' }}>
                    {[a.address_line1, a.address_line2].filter(Boolean).join(' ')}
                  </div>
                )}

                {/* 郵便番号・電話 */}
                <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '6px' }}>
                  〒{a.postal_code}{a.phone_number ? `　${a.phone_number}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* アクションシート */}
      {actionTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={() => setActionTarget(null)}
        >
          <div style={{ padding: '8px', paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', marginBottom: '8px', backdropFilter: 'blur(40px)' }}>
              <div style={{ padding: '14px 16px 10px', textAlign: 'center', borderBottom: '0.5px solid rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fullAddress(actionTarget)}
                </div>
                <div style={{ fontSize: '12px', color: '#8e8e93', marginTop: '2px' }}>〒{actionTarget.postal_code}</div>
              </div>
              <button onClick={() => { setEditing(actionTarget); setModalOpen(true); setActionTarget(null); }} style={actionBtnBase}>変更</button>
              {!actionTarget.is_default && (
                <>
                  <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.1)' }} />
                  <button onClick={() => { setDefault(actionTarget.id); setActionTarget(null); }} disabled={busy} style={actionBtnBase}>デフォルトに設定</button>
                </>
              )}
              <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.1)' }} />
              <button onClick={() => { setDeleteTarget(actionTarget); setActionTarget(null); }} style={{ ...actionBtnBase, color: '#ff3b30' }}>削除</button>
            </div>
            <button onClick={() => setActionTarget(null)} style={{ ...actionBtnBase, background: 'rgba(255,255,255,0.97)', borderRadius: '14px', fontWeight: '600', backdropFilter: 'blur(40px)' }}>キャンセル</button>
          </div>
        </div>
      )}

      {/* 削除確認 */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: '280px', backdropFilter: 'blur(40px)', textAlign: 'center' }}>
            <div style={{ padding: '20px 16px 0' }}>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#000', marginBottom: '8px' }}>この住所を削除</div>
              <div style={{ fontSize: '13px', color: '#8e8e93', marginBottom: '16px', lineHeight: '1.5' }}>
                〒{deleteTarget.postal_code}<br />{fullAddress(deleteTarget)}
              </div>
            </div>
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.15)', display: 'flex' }}>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', borderRight: '0.5px solid rgba(0,0,0,0.15)', fontSize: '17px', color: '#007AFF', cursor: 'pointer' }}>キャンセル</button>
              <button onClick={doDelete} disabled={deleting} style={{ flex: 1, padding: '14px', background: 'none', border: 'none', fontSize: '17px', fontWeight: '600', color: '#ff3b30', cursor: 'pointer' }}>{deleting ? '削除中…' : '削除'}</button>
            </div>
          </div>
        </div>
      )}

      <DeliveryAddressModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} initialAddress={editing} />
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal({ open: false, title: '', message: '' })} />
    </div>
  );
};

export default AddressesPage;
